import './index.css'
import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { deleteAllTransactions, countAllTransactions } from '../../services/transactionService'
import InvitePartnerSection from '../InvitePartnerSection'
import type { Transaction, Card, Category, PlanTier, DbSubscription, DbCoupleInvite } from '../../types'
import { can } from '../../lib/features'
import { NotificationSettings } from '../NotificationSettings'
import { getToastErrorMessage, useToast } from '../Toast'

interface CoupleInviteState {
  pendingInvite: DbCoupleInvite | null
  partnerUserId: string | null
  memberCount: number
  loading: boolean
  error: string | null
  sendInvite: (email: string) => Promise<void>
  cancelInvite: () => Promise<void>
  resendInvite: (email: string) => Promise<void>
  unlinkCouple: () => Promise<string>
}

interface AccountSettingsProps {
  userId: string
  userEmail: string
  householdId: string
  planTier: PlanTier
  subscription: DbSubscription | null
  transactions: Transaction[]
  cards: Card[]
  categories: Category[]
  coupleInvite: CoupleInviteState
  onUnlinked: (newHouseholdId: string) => void
  onAllTransactionsCleared: () => void
  onStartTour?: () => void
  notifications: {
    permission: NotificationPermission
    prefs: { push_enabled: boolean; whatsapp_notify: boolean; days_before: number }
    loading: boolean
    requestPermission: () => Promise<void>
    togglePush: (enabled: boolean) => Promise<void>
    saveDaysBefore: (days: number) => Promise<void>
    toggleWhatsApp: (enabled: boolean) => Promise<void>
  }
  whatsApp: { phone: string | null; loading: boolean; error: string | null }
  onRegisterPhone: (phone: string) => Promise<void>
  onUnregisterPhone: () => Promise<void>
}

export default function AccountSettings({
  userId,
  userEmail,
  householdId,
  planTier,
  subscription,
  transactions,
  cards,
  categories,
  coupleInvite,
  onUnlinked,
  onAllTransactionsCleared,
  onStartTour,
  notifications,
  whatsApp,
  onRegisterPhone,
  onUnregisterPhone,
}: AccountSettingsProps) {
  const toast = useToast()
  // Assinante = quem contratou o plano (subscription.user_id === userId)
  const isAssinante = planTier === 'casal' && subscription?.user_id === userId
  const [deleteStep, setDeleteStep]   = useState<0 | 1 | 2>(0)
  const [deleting, setDeleting]       = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [clearStep, setClearStep]     = useState<0 | 1 | 2>(0)
  const [clearing, setClearing]       = useState(false)
  const [clearError, setClearError]   = useState<string | null>(null)
  const [totalTxCount, setTotalTxCount] = useState<number | null>(null)

  function exportData() {
    const payload = {
      exportedAt: new Date().toISOString(),
      account: { email: userEmail, householdId },
      transactions: transactions.map(t => ({
        id: t.id,
        kind: t.kind,
        title: t.title,
        amount: t.amount,
        category: t.category,
        date: t.date_iso,
        due_date: t.due_date,
        recurring: t.recurring,
        recurring_until: t.recurring_until,
        note: t.note,
      })),
      cards: cards.map(c => ({
        id: c.id,
        name: c.name,
        last4: c.last4,
        limit: c.limit,
        due: c.due,
      })),
      categories: categories.map(cat => ({
        id: cat.id,
        name: cat.name,
        kind: cat.kind,
        spending_limit: cat.spending_limit ?? null,
      })),
    }

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `bagcash-dados-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Dados exportados.')
  }

  async function handleClearStep1() {
    setClearStep(1)
    try {
      const count = await countAllTransactions(householdId)
      setTotalTxCount(count)
    } catch {
      setTotalTxCount(null)
    }
  }

  async function clearAllTransactions() {
    setClearing(true)
    setClearError(null)
    try {
      await deleteAllTransactions(householdId)
      onAllTransactionsCleared()
      setClearStep(0)
      toast.success('Transações apagadas.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao limpar transações. Tente novamente.')
      setClearError(message)
      toast.error(message)
      setClearing(false)
      setClearStep(1)
    }
  }

  async function deleteAccount() {
    setDeleting(true)
    setDeleteError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const { error } = await supabase.functions.invoke('delete-account', {
        body: { userId, householdId },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      if (error) throw error
      toast.success('Conta excluída.')
      await supabase.auth.signOut()
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao excluir conta. Tente novamente.')
      setDeleteError(message)
      toast.error(message)
      setDeleting(false)
      setDeleteStep(1)
    }
  }

  return (
    <div className="bc-account-settings">
      <h2 className="bc-account-settings__title">Minha conta</h2>

      <div className="bc-account-settings__card">
        <div className="bc-account-settings__email-label">E-mail da conta</div>
        <div className="bc-account-settings__email">{userEmail}</div>
      </div>

      {/* Tour do app */}
      {onStartTour && (
        <div className="bc-account-settings__card">
          <div className="bc-account-settings__row">
            <div>
              <div className="bc-account-settings__item-title">Tour do app</div>
              <div className="bc-account-settings__item-desc">
                Refaça o tour interativo de apresentação do BagCash.
              </div>
            </div>
            <button className="bc-btn bc-btn--secondary" onClick={onStartTour}>
              Iniciar tour
            </button>
          </div>
        </div>
      )}

      {planTier !== 'free' && (
        <NotificationSettings
          permission={notifications.permission}
          prefs={notifications.prefs}
          planTier={planTier}
          hasWhatsApp={whatsApp.phone !== null}
          onRequestPermission={notifications.requestPermission}
          onTogglePush={notifications.togglePush}
          onSaveDaysBefore={notifications.saveDaysBefore}
          onToggleWhatsApp={notifications.toggleWhatsApp}
        />
      )}
      {can('whatsapp_bot', planTier) && (
        <WhatsAppBotSection
          registeredPhone={whatsApp.phone}
          error={whatsApp.error}
          onRegister={onRegisterPhone}
          onUnregister={onUnregisterPhone}
        />
      )}

      {/* Exportar dados */}
      <div className="bc-account-settings__card">
        <div className="bc-account-settings__row">
          <div>
            <div className="bc-account-settings__item-title">Exportar meus dados</div>
            <div className="bc-account-settings__item-desc">
              Baixe todas as suas transações, cartões e categorias em formato JSON
              (Art. 18, V da LGPD — direito à portabilidade).
            </div>
          </div>
          <button className="bc-btn bc-btn--secondary" onClick={exportData}>
            Exportar
          </button>
        </div>
      </div>

      {/* Conta Casal — visível apenas para o assinante */}
      {isAssinante && (
        <div id="invite-partner-section">
          <InvitePartnerSection
            pendingInvite={coupleInvite.pendingInvite}
            partnerUserId={coupleInvite.partnerUserId}
            memberCount={coupleInvite.memberCount}
            loading={coupleInvite.loading}
            hookError={coupleInvite.error}
            sendInvite={coupleInvite.sendInvite}
            cancelInvite={coupleInvite.cancelInvite}
            resendInvite={coupleInvite.resendInvite}
            unlinkCouple={coupleInvite.unlinkCouple}
            onUnlinked={onUnlinked}
          />
        </div>
      )}

      {/* Links legais */}
      <div className="bc-account-settings__card">
        <div className="bc-account-settings__item-title">Documentos legais</div>
        <div className="bc-account-settings__legal-links">
          <a href="/privacidade" className="bc-account-settings__legal-link">
            Política de Privacidade
          </a>
          <span className="bc-account-settings__legal-sep">·</span>
          <a href="/termos" className="bc-account-settings__legal-link">
            Termos de Uso
          </a>
        </div>
        <div className="bc-account-settings__item-desc" style={{ marginTop: '0.5rem' }}>
          Dúvidas sobre seus dados:{' '}
          <a href="mailto:privacidade@bagcash.app">privacidade@bagcash.app</a>
        </div>
      </div>

      {/* Excluir conta */}
      <div className="bc-account-settings__card bc-account-settings__card--danger">
        <div className="bc-account-settings__item-title bc-account-settings__item-title--danger">
          Zona de perigo
        </div>

        {clearStep === 0 && (
          <div className="bc-account-settings__row">
            <div className="bc-account-settings__item-desc">
              Apaga permanentemente todas as suas transações de todos os meses. Cartões,
              categorias e objetivos não são afetados.
            </div>
            <button
              className="bc-btn bc-btn--danger"
              onClick={handleClearStep1}
            >
              Limpar transações
            </button>
          </div>
        )}

        {clearStep === 1 && (
          <div className="bc-account-settings__confirm">
            <p className="bc-account-settings__confirm-text">
              Tem certeza? Esta ação é <strong>irreversível</strong>. Todas as suas
              transações serão apagadas permanentemente.
            </p>
            {clearError && (
              <p className="bc-account-settings__error">{clearError}</p>
            )}
            <div className="bc-account-settings__confirm-actions">
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => { setClearStep(0); setClearError(null) }}
                disabled={clearing}
              >
                Cancelar
              </button>
              <button
                className="bc-btn bc-btn--danger"
                onClick={() => setClearStep(2)}
                disabled={clearing}
              >
                Sim, limpar tudo
              </button>
            </div>
          </div>
        )}

        {clearStep === 2 && (
          <div className="bc-account-settings__confirm">
            <p className="bc-account-settings__confirm-text">
              Última confirmação: você está prestes a apagar{' '}
              <strong>
                {totalTxCount !== null ? `${totalTxCount} transações` : 'todas as transações'}
              </strong>{' '}
              de todos os meses.
            </p>
            {clearError && (
              <p className="bc-account-settings__error">{clearError}</p>
            )}
            <div className="bc-account-settings__confirm-actions">
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => { setClearStep(0); setClearError(null) }}
                disabled={clearing}
              >
                Cancelar
              </button>
              <button
                className="bc-btn bc-btn--danger"
                onClick={clearAllTransactions}
                disabled={clearing}
              >
                {clearing ? 'Limpando…' : 'Confirmar limpeza'}
              </button>
            </div>
          </div>
        )}

        {deleteStep === 0 && clearStep === 0 && <hr style={{ border: 'none', borderTop: '1px solid var(--expense-200, #f5c6c0)', margin: '0.75rem 0' }} />}

        {deleteStep === 0 && (
          <div className="bc-account-settings__row">
            <div className="bc-account-settings__item-desc">
              Excluir sua conta remove permanentemente todos os seus dados financeiros.
              Dados de pagamento são anonimizados conforme exigência legal.
            </div>
            <button
              className="bc-btn bc-btn--danger"
              onClick={() => setDeleteStep(1)}
            >
              Excluir conta
            </button>
          </div>
        )}

        {deleteStep === 1 && (
          <div className="bc-account-settings__confirm">
            <p className="bc-account-settings__confirm-text">
              Tem certeza? Esta ação é <strong>irreversível</strong>. Todos os seus dados
              (transações, cartões, categorias) serão excluídos permanentemente.
            </p>
            {deleteError && (
              <p className="bc-account-settings__error">{deleteError}</p>
            )}
            <div className="bc-account-settings__confirm-actions">
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => { setDeleteStep(0); setDeleteError(null) }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="bc-btn bc-btn--danger"
                onClick={() => setDeleteStep(2)}
                disabled={deleting}
              >
                Sim, excluir minha conta
              </button>
            </div>
          </div>
        )}

        {deleteStep === 2 && (
          <div className="bc-account-settings__confirm">
            <p className="bc-account-settings__confirm-text">
              Última confirmação: você perderá acesso a <strong>{transactions.length}</strong> transações
              e <strong>{cards.length}</strong> cartões cadastrados.
            </p>
            {deleteError && (
              <p className="bc-account-settings__error">{deleteError}</p>
            )}
            <div className="bc-account-settings__confirm-actions">
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => { setDeleteStep(0); setDeleteError(null) }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="bc-btn bc-btn--danger"
                onClick={deleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Excluindo…' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function WhatsAppBotSection({
  registeredPhone,
  error,
  onRegister,
  onUnregister,
}: {
  registeredPhone: string | null
  error: string | null
  onRegister: (phone: string) => Promise<void>
  onUnregister: () => Promise<void>
}) {
  const toast = useToast()
  const [ddd, setDdd] = useState('')
  const [number, setNumber] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!/^\d{2}$/.test(ddd)) {
      setLocalError('DDD inválido')
      toast.warning('Informe um DDD válido.')
      return
    }
    if (!/^\d{8,9}$/.test(number)) {
      setLocalError('Número inválido')
      toast.warning('Informe um número válido.')
      return
    }
    setLocalError(null)
    try {
      await onRegister(`+55${ddd}${number}`)
      toast.success('Número cadastrado.')
    } catch (e) {
      const message = getToastErrorMessage(e, 'Erro ao cadastrar número.')
      setLocalError(message)
      toast.error(message)
    }
  }

  async function handleUnregister() {
    try {
      await onUnregister()
      toast.success('Número removido.')
    } catch (e) {
      toast.error(getToastErrorMessage(e, 'Erro ao remover número.'))
    }
  }

  return (
    <div id="whatsapp-bot" className="bc-account-settings__card">
      <div className="bc-account-settings__item-title">WhatsApp Bot</div>
      {!registeredPhone ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>+55</span>
            <input
              data-testid="input-ddd"
              type="tel"
              maxLength={2}
              placeholder="DDD"
              value={ddd}
              onChange={e => setDdd(e.target.value.replace(/\D/g, ''))}
              style={{ width: '60px', padding: '6px', borderRadius: '4px', border: '1px solid var(--sand-4)' }}
            />
            <input
              data-testid="input-number"
              type="tel"
              maxLength={9}
              placeholder="Número"
              value={number}
              onChange={e => setNumber(e.target.value.replace(/\D/g, ''))}
              style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid var(--sand-4)' }}
            />
          </div>
          <button className="bc-btn bc-btn--primary" onClick={handleSave} style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}>Salvar número</button>
          {(localError ?? error) && <p className="bc-account-settings__error">{localError ?? error}</p>}
        </div>
      ) : (
        <div className="bc-account-settings__row" style={{ marginTop: '1rem' }}>
          <span className="bc-account-settings__item-desc" style={{ color: 'var(--income-600)', fontWeight: '500' }}>
            ✓ Número registrado: {registeredPhone}
          </span>
          <button className="bc-btn bc-btn--secondary" onClick={handleUnregister}>Remover</button>
        </div>
      )}
    </div>
  )
}
